// src/services/supplierService.js (supplier)
import api from './api';

/**
 * Servicio para gestionar operaciones CRUD de proveedores
 * Incluye filtrado automático por empresa_id del usuario autenticado
 */
export const supplierService = {
  /**
   * Obtiene la lista de proveedores con filtros opcionales
   * @param {Object} params - Parámetros de consulta
   * @param {string} params.orden - Campo de ordenamiento (id, business_name, created_at)
   * @param {string} params.dir - Dirección del orden (ASC, DESC)
   * @param {number} params.limit - Límite de registros
   * @param {number} params.offset - Offset para paginación
   * @param {Object} params.filtros - Filtros adicionales
   * @returns {Promise} Lista de proveedores
   */
  async getSuppliers(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      // Agregar parámetros de paginación y ordenamiento
      if (params.orden) queryParams.append('orden', params.orden);
      if (params.dir) queryParams.append('dir', params.dir);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);

      // Agregar filtros si existen
      if (params.filtros && Object.keys(params.filtros).length > 0) {
        queryParams.append('filtros', JSON.stringify(params.filtros));
      }

      const queryString = queryParams.toString();
      const url = `/proveedores${queryString ? `?${queryString}` : ''}`;

      console.log('[DEBUG Supplier Service] Getting suppliers:', url);
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('[ERROR Supplier Service] Error getting suppliers:', error);
      throw error;
    }
  },

  /**
   * Obtiene un proveedor específico por ID
   * @param {number} id - ID del proveedor
   * @returns {Promise} Datos del proveedor
   */
  async getSupplier(id) {
    try {
      console.log('[DEBUG Supplier Service] Getting supplier:', id);
      const response = await api.get(`/proveedores/${id}`);
      return response.data;
    } catch (error) {
      console.error('[ERROR Supplier Service] Error getting supplier:', error);
      throw error;
    }
  },

  /**
   * Crea un nuevo proveedor
   * @param {Object} supplierData - Datos del proveedor
   * @param {string} supplierData.business_name - Nombre del proveedor
   * @param {string} supplierData.identification_type_sri - Tipo de identificación (04=RUC, 05=Cédula)
   * @param {string} supplierData.identification_number - Número de identificación
   * @param {string} supplierData.email - Email (opcional)
   * @param {string} supplierData.phone - Teléfono (opcional)
   * @param {string} supplierData.address - Dirección (opcional)
   * @param {boolean} supplierData.is_active - Estado activo (opcional, default true)
   * @returns {Promise} ID del proveedor creado
   */
  async createSupplier(supplierData) {
    try {
      console.log('[DEBUG Supplier Service] Creating supplier:', supplierData);
      const response = await api.post('/proveedores', supplierData);
      return response.data;
    } catch (error) {
      console.error('[ERROR Supplier Service] Error creating supplier:', error);
      throw error;
    }
  },

  /**
   * Actualiza un proveedor existente
   * @param {number} id - ID del proveedor
   * @param {Object} supplierData - Datos a actualizar
   * @returns {Promise} Confirmación de actualización
   */
  async updateSupplier(id, supplierData) {
    try {
      console.log('[DEBUG Supplier Service] Updating supplier:', id, supplierData);
      const response = await api.put(`/proveedores/${id}`, supplierData);
      return response.data;
    } catch (error) {
      console.error('[ERROR Supplier Service] Error updating supplier:', error);
      throw error;
    }
  },

  /**
   * Elimina (desactiva) un proveedor
   * @param {number} id - ID del proveedor
   * @returns {Promise} Confirmación de eliminación
   */
  async deleteSupplier(id) {
    try {
      console.log('[DEBUG Supplier Service] Deleting supplier:', id);
      const response = await api.delete(`/proveedores/${id}`);
      return response.data;
    } catch (error) {
      console.error('[ERROR Supplier Service] Error deleting supplier:', error);
      throw error;
    }
  },

  /**
   * Valida una identificación (RUC/Cédula)
   * @param {string} identification - Número de identificación
   * @returns {Promise} Resultado de validación { valid: boolean, type: string }
   */
  async validateIdentification(identification) {
    try {
      const response = await api.get(`/validation/identification?identification=${identification}`);
      return response.data;
    } catch (error) {
      console.error('[ERROR Supplier Service] Error validating identification:', error);
      throw error;
    }
  }
};

export default supplierService;